import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { assignmentsAPI } from '../api/assignments'
import type { MyAssignment } from '../api/assignments'
import { Button, Card, Badge, StatusBadge, PageLayout } from '../components/ui'

// ── Types ────────────────────────────────────────────────────────────

interface DashboardStats {
  active_surveys: number
  total_responses: number
  total_members: number
  avg_impact_score: number | null
}

interface RecentSurvey {
  id: string
  title: string
  organization_name: string
  status: string
  response_count: number
  created_at: string
}

interface DashboardOrg {
  id: string
  name: string
  member_count?: number
  role?: string
}

// Admin dashboard format
interface AdminDashboardData {
  stats: DashboardStats
  recent_surveys: RecentSurvey[]
  organizations: DashboardOrg[]
}

// Respondent dashboard format
interface RespondentSurvey {
  id: string
  public_id: string | null
  title: string
  organization_name: string
  attempts: number
  latest_score: number | null
  delta: number | null
  last_taken: string | null
  next_due: string | null
  can_take: boolean
}

interface RespondentDashboardData {
  is_respondent: true
  my_surveys: RespondentSurvey[]
  organizations: DashboardOrg[]
}

type DashboardData = AdminDashboardData | RespondentDashboardData

function isRespondentData(data: DashboardData): data is RespondentDashboardData {
  return 'is_respondent' in data && data.is_respondent === true
}

const API_BASE = API_ENDPOINTS.surveys.base.replace('/surveys', '')

// ── Main Page ────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [myAssignments, setMyAssignments] = useState<MyAssignment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const [statsRes, assignmentsRes] = await Promise.all([
          fetch(`${API_BASE}/dashboard/stats`, {
            headers: { 'Authorization': token ? `Bearer ${token}` : '' }
          }),
          assignmentsAPI.getMyAssignments().catch(() => [] as MyAssignment[]),
        ])
        if (statsRes.ok) setData(await statsRes.json())
        setMyAssignments(assignmentsRes)
      } catch (err) {
        console.error('Failed to fetch dashboard:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchDashboard()
  }, [])

  if (loading) {
    return (
      <PageLayout maxWidth="xl">
        <div className="text-center py-12 text-foreground-muted"><FormattedMessage id="dashboard.loading" /></div>
      </PageLayout>
    )
  }

  if (!data) {
    return (
      <PageLayout maxWidth="xl">
        <div className="text-center py-12 text-foreground-muted"><FormattedMessage id="dashboard.loadFailed" /></div>
      </PageLayout>
    )
  }

  // Branch based on role
  if (isRespondentData(data)) {
    return <RespondentDashboard data={data} userName={user?.full_name} />
  }

  return <AdminDashboard data={data} user={user} myAssignments={myAssignments} />
}

// ── Respondent Dashboard ─────────────────────────────────────────────

const RespondentDashboard: React.FC<{
  data: RespondentDashboardData
  userName?: string
}> = ({ data, userName }) => {
  const intl = useIntl()
  return (
    <PageLayout maxWidth="xl">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          <FormattedMessage id="dashboard.welcomeBack" values={{ name: userName?.split(' ')[0] || intl.formatMessage({ id: 'dashboard.thereFallback' }) }} />
        </h1>
        <p className="mt-1 text-sm text-foreground-muted"><FormattedMessage id="dashboard.respondentSubtitle" /></p>
      </div>

      <div className="space-y-6 lg:space-y-8">
        {/* Survey Cards */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4"><FormattedMessage id="dashboard.yourSurveys" /></h2>
          {data.my_surveys.length === 0 ? (
            <Card>
              <div className="text-center py-8 text-foreground-muted">
                <p className="text-sm"><FormattedMessage id="dashboard.noSurveysAssigned" /></p>
                <p className="text-xs mt-1"><FormattedMessage id="dashboard.noSurveysHint" /></p>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.my_surveys.map((survey) => (
                <RespondentSurveyCard key={survey.id} survey={survey} />
              ))}
            </div>
          )}
        </div>

        {/* Organizations */}
        {data.organizations.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              <FormattedMessage id="dashboard.yourOrganizationsTitle" values={{ count: data.organizations.length }} />
            </h2>
            <Card padding="none">
              <ul className="divide-y divide-border-light">
                {data.organizations.map((org) => (
                  <li key={org.id} className="px-4 py-3">
                    <p className="text-sm font-medium text-foreground">{org.name}</p>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </PageLayout>
  )
}

const RespondentSurveyCard: React.FC<{ survey: RespondentSurvey }> = ({ survey }) => {
  const hasScore = survey.latest_score !== null
  const hasDelta = survey.delta !== null
  const deltaPositive = hasDelta && survey.delta! > 0
  const deltaNegative = hasDelta && survey.delta! < 0

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="p-4 space-y-3">
        {/* Title & Org */}
        <div>
          <h3 className="text-sm font-semibold text-foreground">{survey.title}</h3>
          <p className="text-xs text-foreground-muted">{survey.organization_name}</p>
        </div>

        {/* Stats */}
        <div className="space-y-1.5">
          {survey.last_taken ? (
            <p className="text-xs text-foreground-muted">
              <FormattedMessage id="dashboard.lastTaken" values={{ date: new Date(survey.last_taken).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }} />
            </p>
          ) : (
            <p className="text-xs text-foreground-muted"><FormattedMessage id="dashboard.notYetTaken" /></p>
          )}

          {hasScore && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                <FormattedMessage id="dashboard.score" values={{ score: survey.latest_score }} />
              </span>
              {hasDelta && (
                <span className={`text-xs font-medium ${
                  deltaPositive ? 'text-success' : deltaNegative ? 'text-error' : 'text-foreground-muted'
                }`}>
                  <FormattedMessage id="dashboard.deltaSinceFirst" values={{ delta: `${deltaPositive ? '+' : ''}${survey.delta}` }} />
                </span>
              )}
            </div>
          )}

          {survey.next_due && (
            <p className="text-xs text-foreground-muted">
              <FormattedMessage id="dashboard.nextDue" values={{ date: new Date(survey.next_due).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }} />
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          {survey.attempts > 0 && (
            <Link to={`/surveys/${survey.id}/responses`}>
              <Button variant="outline" size="sm"><FormattedMessage id="dashboard.viewResults" /></Button>
            </Link>
          )}
          {survey.can_take && survey.public_id && (
            <Link to={`/survey/${survey.public_id}`}>
              <Button variant="primary" size="sm">
                {survey.attempts > 0 ? <FormattedMessage id="dashboard.retake" /> : <FormattedMessage id="dashboard.takeSurvey" />}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Card>
  )
}

// ── Admin Dashboard ──────────────────────────────────────────────────

const AdminDashboard: React.FC<{
  data: AdminDashboardData
  user: any
  myAssignments: MyAssignment[]
}> = ({ data, user, myAssignments }) => {
  const intl = useIntl()
  return (
    <PageLayout maxWidth="xl">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          <FormattedMessage id="dashboard.welcomeBack" values={{ name: user?.full_name?.split(' ')[0] || intl.formatMessage({ id: 'dashboard.thereFallback' }) }} />
        </h1>
        <p className="mt-1 text-sm text-foreground-muted"><FormattedMessage id="dashboard.adminSubtitle" /></p>

        {/* Actions: grid on mobile, inline on lg */}
        <div className="mt-4 grid grid-cols-2 gap-2 lg:flex lg:gap-3">
          <Link to="/surveys/new">
            <Button variant="primary" size="sm" fullWidth className="lg:w-auto"><FormattedMessage id="dashboard.createSurvey" /></Button>
          </Link>
          <Link to="/impact">
            <Button variant="outline" size="sm" fullWidth className="lg:w-auto"><FormattedMessage id="dashboard.impactReport" /></Button>
          </Link>
          <Link to="/surveys/templates">
            <Button variant="outline" size="sm" fullWidth className="lg:w-auto"><FormattedMessage id="dashboard.templates" /></Button>
          </Link>
        </div>
      </div>

      <div className="space-y-6 lg:space-y-8">
        {/* Stats Cards: 2 cols on mobile, 4 on lg */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          <StatCard
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
            iconBg="bg-primary/20"
            iconColor="text-primary"
            label={intl.formatMessage({ id: 'dashboard.activeSurveys' })}
            value={data.stats.active_surveys}
          />
          <StatCard
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />}
            iconBg="bg-success-light"
            iconColor="text-success"
            label={intl.formatMessage({ id: 'dashboard.responses' })}
            value={data.stats.total_responses}
          />
          <StatCard
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />}
            iconBg="bg-warning-light"
            iconColor="text-warning"
            label={intl.formatMessage({ id: 'dashboard.members' })}
            value={data.stats.total_members}
          />
          <StatCard
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />}
            iconBg="bg-primary/20"
            iconColor="text-secondary-dark"
            label={intl.formatMessage({ id: 'dashboard.avgImpact' })}
            value={data.stats.avg_impact_score != null ? `${data.stats.avg_impact_score}%` : '--'}
          />
        </div>

        {/* Recent Surveys */}
        <SurveyList surveys={data.recent_surveys} />

        {/* Assigned Surveys */}
        {myAssignments.length > 0 && (
          <AssignmentList assignments={myAssignments} />
        )}

        {/* Organizations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card padding="none">
            <div className="px-4 py-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground"><FormattedMessage id="dashboard.myOrganizations" /></h3>
              <Link to="/organizations" className="text-sm text-primary"><FormattedMessage id="dashboard.viewAll" /></Link>
            </div>
            <div className="border-t border-border">
              {data.organizations.length === 0 ? (
                <div className="px-4 py-5 text-center">
                  <p className="text-sm text-foreground-muted mb-3"><FormattedMessage id="dashboard.notPartOfOrg" /></p>
                  <Link to="/organizations/new" className="text-sm text-primary"><FormattedMessage id="dashboard.createOrganization" /></Link>
                </div>
              ) : (
                <ul className="divide-y divide-border-light">
                  {data.organizations.map((org) => (
                    <li key={org.id}>
                      <Link to={`/organizations/${org.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-background-alt transition-colors">
                        <div>
                          <p className="text-sm font-medium text-foreground">{org.name}</p>
                          <p className="text-xs text-foreground-muted"><FormattedMessage id="dashboard.memberCount" values={{ count: org.member_count }} /></p>
                        </div>
                        <StatusBadge status={org.role || 'RESPONDENT'} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  )
}

// ── Shared Components ────────────────────────────────────────────────

function StatCard({ icon, iconBg, iconColor, label, value }: {
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  label: string
  value: string | number
}) {
  return (
    <Card padding="none" className="p-3 sm:p-4 lg:p-5">
      {/* Vertical on mobile, horizontal on sm+ */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className={`flex-shrink-0 ${iconBg} rounded-md p-2 lg:p-3 self-start`}>
          <svg className={`h-5 w-5 lg:h-6 lg:w-6 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {icon}
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-xs lg:text-sm font-medium text-foreground-muted">{label}</p>
          <p className="text-lg lg:text-2xl font-bold text-foreground">{value}</p>
        </div>
      </div>
    </Card>
  )
}

function SurveyList({ surveys }: { surveys: RecentSurvey[] }) {
  return (
    <Card padding="none">
      <div className="px-4 py-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground"><FormattedMessage id="dashboard.recentSurveys" /></h3>
        <Link to="/surveys/new" className="text-sm text-primary"><FormattedMessage id="dashboard.createNew" /></Link>
      </div>
      {surveys.length === 0 ? (
        <div className="px-4 py-8 text-center text-foreground-muted border-t border-border">
          <p className="text-sm"><FormattedMessage id="dashboard.noSurveysYet" /></p>
          <Link to="/surveys/new" className="mt-2 inline-block text-sm text-primary"><FormattedMessage id="dashboard.createFirstSurvey" /></Link>
        </div>
      ) : (
        <div className="border-t border-border divide-y divide-border-light">
          {surveys.map((survey) => (
            <div key={survey.id} className="px-4 py-3 flex items-center gap-3 hover:bg-background-alt transition-colors">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{survey.title}</p>
                <p className="text-xs text-foreground-muted truncate">{survey.organization_name}</p>
              </div>
              <StatusBadge status={survey.status} />
              <span className="hidden sm:inline text-xs text-foreground-muted whitespace-nowrap"><FormattedMessage id="dashboard.respCount" values={{ count: survey.response_count }} /></span>
              <span className="hidden md:inline text-xs text-foreground-muted whitespace-nowrap">{new Date(survey.created_at).toLocaleDateString()}</span>
              <div className="flex gap-2 flex-shrink-0">
                <Link to={`/surveys/${survey.id}/share`} className="text-xs text-primary font-medium"><FormattedMessage id="dashboard.view" /></Link>
                <Link to={`/surveys/${survey.id}/responses`} className="hidden sm:inline text-xs text-foreground-muted"><FormattedMessage id="dashboard.responses" /></Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function AssignmentList({ assignments }: { assignments: MyAssignment[] }) {
  return (
    <Card padding="none">
      <div className="px-4 py-4">
        <h3 className="text-base font-semibold text-foreground"><FormattedMessage id="dashboard.myAssignedSurveys" /></h3>
      </div>
      <div className="border-t border-border divide-y divide-border-light">
        {assignments.map((a) => (
          <div key={a.id} className="px-4 py-3 flex items-center gap-3 hover:bg-background-alt transition-colors">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{a.survey_title}</p>
              <p className="text-xs text-foreground-muted truncate">{a.organization_name}</p>
            </div>
            <Badge variant={a.status === 'COMPLETED' ? 'success' : 'warning'}>{a.status}</Badge>
            <span className="hidden sm:inline text-xs text-foreground-muted whitespace-nowrap">
              {new Date(a.assigned_at).toLocaleDateString()}
            </span>
            <div className="flex-shrink-0">
              {a.status === 'PENDING' && a.survey_status === 'PUBLISHED' && a.survey_public_id ? (
                <Link to={`/survey/${a.survey_public_id}`} className="text-xs text-primary font-medium"><FormattedMessage id="dashboard.take" /></Link>
              ) : a.status === 'COMPLETED' ? (
                <span className="text-xs text-success"><FormattedMessage id="dashboard.done" /></span>
              ) : (
                <span className="text-xs text-foreground-muted"><FormattedMessage id="dashboard.na" /></span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default DashboardPage
